import React, { useState, useEffect } from 'react';
import {
  Container,
  Typography,
  Box,
  Alert,
  Button,
  Paper,
  CircularProgress
} from '@mui/material';
import {
  Email as EmailIcon,
  CheckCircle as CheckCircleIcon,
  Refresh as RefreshIcon
} from '@mui/icons-material';
import { sendEmailVerification, onAuthStateChanged } from 'firebase/auth';
import { auth } from '../../firebase/config';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { useTranslation } from '../../hooks/useTranslation';

const EmailVerification = ({ userEmail, userType }) => {
  const [isVerified, setIsVerified] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [checkingVerification, setCheckingVerification] = useState(true);
  
  const navigate = useNavigate();
  const { currentUser, refreshUserProfile } = useAuth();
  const { t } = useTranslation();

  useEffect(() => {
    // Check verification status on mount
    if (currentUser) {
      setIsVerified(currentUser.emailVerified);
      setCheckingVerification(false);
      
      // Si déjà vérifié, rediriger immédiatement
      if (currentUser.emailVerified) {
        setSuccess(t('auth.emailVerifiedSuccess'));
        setTimeout(() => {
          if (userType === 'client') {
            navigate('/');
          } else {
            navigate('/dashboard');
          }
        }, 2000);
      }
    }

    // Listen for auth state changes to detect verification
    const unsubscribe = onAuthStateChanged(auth, (user) => {
      if (user) {
        // Reload user to get latest verification status
        user.reload().then(() => {
          const wasVerified = isVerified;
          setIsVerified(user.emailVerified);
          setCheckingVerification(false);
          
          // Seulement rediriger si la vérification vient de se faire (pas déjà vérifié)
          if (user.emailVerified && !wasVerified) {
            setSuccess(t('auth.emailVerifiedSuccess'));
            // Rafraîchir le profil utilisateur dans le contexte
            refreshUserProfile();
            setTimeout(() => {
              // Redirect based on user type
              if (userType === 'client') {
                navigate('/');
              } else {
                navigate('/dashboard');
              }
            }, 2000);
          }
        });
      } else {
        setCheckingVerification(false);
      }
    });

    return () => unsubscribe();
  }, [currentUser, navigate, userType, isVerified]);

  const resendVerificationEmail = async () => {
    if (!currentUser) {
      setError(t('auth.noUserConnected'));
      return;
    }

    setLoading(true);
    setError('');

    try {
      await sendEmailVerification(currentUser);
      setSuccess(t('auth.verificationEmailResent'));
    } catch (error) {
      console.error('Error sending verification email:', error);
      setError(t('auth.errorSendingVerificationEmail'));
    } finally {
      setLoading(false);
    }
  };

  const checkVerificationStatus = async () => {
    if (!currentUser) return;

    setCheckingVerification(true);
    setError('');
    
    try {
      // Forcer le rechargement de l'utilisateur depuis Firebase
      await currentUser.reload();
      
      // Récupérer l'utilisateur mis à jour
      const updatedUser = auth.currentUser;
      if (updatedUser) {
        setIsVerified(updatedUser.emailVerified);
        
        if (updatedUser.emailVerified) {
          setSuccess(t('auth.emailVerifiedSuccess'));
          // Rafraîchir le profil utilisateur dans le contexte
          await refreshUserProfile();
          setTimeout(() => {
            if (userType === 'client') {
              navigate('/');
            } else {
              navigate('/dashboard');
            }
          }, 2000);
        } else {
          setError(t('auth.emailNotYetVerified'));
        }
      }
    } catch (error) {
      console.error('Error checking verification:', error);
      setError(t('auth.verificationError'));
    } finally {
      setCheckingVerification(false);
    }
  };

  if (isVerified) {
    return (
      <Container maxWidth="sm" sx={{ py: 8 }}>
        <Paper elevation={0} sx={{ p: 4, textAlign: 'center', border: '1px solid #e0e0e0' }}>
          <CheckCircleIcon sx={{ fontSize: 64, color: 'success.main', mb: 2 }} />
          <Typography variant="h4" gutterBottom sx={{ fontWeight: 'bold' }}>
            {t('auth.emailVerified')}
          </Typography>
          <Typography variant="body1" color="text.secondary" sx={{ mb: 3 }}>
            {t('auth.accountCreatedRedirecting')}
          </Typography>
          <CircularProgress />
        </Paper>
      </Container>
    );
  }

  return (
    <Container maxWidth="sm" sx={{ py: 8 }}>
      <Paper elevation={0} sx={{ p: 4, textAlign: 'center', border: '1px solid #e0e0e0' }}>
        <EmailIcon sx={{ fontSize: 64, color: 'primary.main', mb: 2 }} />
        
        <Typography variant="h4" gutterBottom sx={{ fontWeight: 'bold' }}>
          {t('auth.verifyYourEmail')}
        </Typography>
        
        <Typography variant="body1" color="text.secondary" sx={{ mb: 3 }}>
          {t('auth.verificationLinkSentTo')}
        </Typography>
        
        <Typography variant="h6" sx={{ mb: 3, fontWeight: 'bold' }}>
          {userEmail}
        </Typography>
        
        <Typography variant="body2" color="text.secondary" sx={{ mb: 4 }}>
          {t('auth.clickLinkToActivate')}
        </Typography>

        {error && (
          <Alert severity="error" sx={{ mb: 3 }}>
            {error}
          </Alert>
        )}

        {success && (
          <Alert severity="success" sx={{ mb: 3 }}>
            {success}
          </Alert>
        )}

        <Box display="flex" flexDirection="column" gap={2}>
          <Button
            variant="contained"
            onClick={checkVerificationStatus}
            disabled={checkingVerification}
            startIcon={checkingVerification ? <CircularProgress size={20} /> : <RefreshIcon />}
            sx={{ 
              backgroundColor: '#212121',
              color: 'white',
              '&:hover': {
                backgroundColor: '#424242'
              }
            }}
          >
            {checkingVerification ? t('auth.verifying') : t('auth.iVerifiedMyEmail')}
          </Button>
          
          <Button
            variant="outlined"
            onClick={resendVerificationEmail}
            disabled={loading}
            startIcon={loading ? <CircularProgress size={20} /> : <EmailIcon />}
          >
            {loading ? t('auth.sending') : t('auth.resendEmail')}
          </Button>
          
          <Button
            variant="text"
            onClick={() => navigate('/')}
            sx={{ color: 'text.secondary' }}
          >
            {t('auth.backToHome')}
          </Button>
        </Box>

        <Box mt={4} p={2} sx={{ backgroundColor: '#f5f5f5', borderRadius: 1 }}>
          <Typography variant="body2" color="text.secondary">
            <strong>{t('auth.emailProblem')}</strong><br/>
            • {t('auth.checkSpamFolder')}<br/>
            • {t('auth.ensureEmailCorrect')}<br/>
            • {t('auth.contactSupportIfPersists')}
          </Typography>
        </Box>
      </Paper>
    </Container>
  );
};

export default EmailVerification;